import { z } from "zod";

export const AUDIT_LOG_ACTIONS = ["create", "update", "delete"] as const;
export const AUDIT_LOG_DOMAINS = ["role", "integration", "user"] as const;
export const AUDIT_LOG_ACTOR_TYPES = ["human", "service"] as const;

export type AuditLogAction = (typeof AUDIT_LOG_ACTIONS)[number];
export type AuditLogDomain = (typeof AUDIT_LOG_DOMAINS)[number];
export type AuditLogActorType = (typeof AUDIT_LOG_ACTOR_TYPES)[number];

const absentAsNull = <T extends z.ZodTypeAny>(schema: T) => schema.nullable().default(null);

const nullableString = () => absentAsNull(z.string());
const nullableJsonObject = () => absentAsNull(z.record(z.string(), z.unknown()));

export const AuditLogEntrySchema = z.object({
    id: z.string(),
    occurredAt: z.string(),
    action: z.enum(AUDIT_LOG_ACTIONS),
    domain: absentAsNull(z.enum(AUDIT_LOG_DOMAINS)),
    entityType: z.string(),
    entityClass: nullableString(),
    entityId: z.string().default(""),
    entityLabel: nullableString(),
    actorType: nullableString(),
    actorId: nullableString(),
    actorLabel: nullableString(),
    recipientLabel: nullableString(),
    rootType: nullableString(),
    rootId: nullableString(),
    rootLabel: nullableString(),
    changes: nullableJsonObject(),
    ip: nullableString(),
    requestId: nullableString(),
    metadata: nullableJsonObject(),
});

export const AuditLogFacetCountSchema = z.object({
    value: z.string(),
    label: nullableString(),
    count: z.number(),
});

export const AuditLogDayCountSchema = z.object({
    day: z.string(),
    count: z.number(),
});

export const AuditLogFacetsSchema = z.object({
    actions: AuditLogFacetCountSchema.array(),
    entityTypes: AuditLogFacetCountSchema.array(),
    actors: AuditLogFacetCountSchema.array(),
    actorTypes: AuditLogFacetCountSchema.array(),
    scopes: AuditLogFacetCountSchema.array(),
    perDay: AuditLogDayCountSchema.array(),
});

export const AuditLogFeedSchema = z.object({
    items: AuditLogEntrySchema.array(),
    nextCursor: nullableString(),
    facets: AuditLogFacetsSchema,
});

export type AuditLogEntry = z.infer<typeof AuditLogEntrySchema>;
export type AuditLogFacetCount = z.infer<typeof AuditLogFacetCountSchema>;
export type AuditLogDayCount = z.infer<typeof AuditLogDayCountSchema>;
export type AuditLogFacets = z.infer<typeof AuditLogFacetsSchema>;
export type AuditLogFeed = z.infer<typeof AuditLogFeedSchema>;
